import { useKrBacktest } from '../../hooks/useKrBacktest';
import { KrBacktestSummary } from '../../services/krBacktestApi';

export default function KrBacktestPanel({ showHeader = false }: KrBacktestPanelProps) {
  const state = useKrBacktest();

  return (
    <>
      {showHeader && (
        <div className="backtest-header">
          <span className="eyebrow">KR Replay / Paper Simulation</span>
          <h1 className="backtest-title">국내 주식 백테스트</h1>
          <p className="kr-backtest-note">
            Toss KR 실제 일봉만 사용합니다. 결과가 없으면 데이터 없음/거래 없음으로 표시합니다.
          </p>
        </div>
      )}
      {state.error && <div className="backtest-error-banner">API 오류: {state.error}</div>}
      {state.notice && <div className="kr-backtest-notice">{state.notice}</div>}
      <section className="kr-backtest-card">
        <div className="kr-backtest-grid">
          <Field label="종목" value={state.symbols} onChange={state.setSymbols} />
          <Field label="시작일" value={state.startDate} onChange={state.setStartDate} type="date" />
          <Field label="종료일" value={state.endDate} onChange={state.setEndDate} type="date" />
          <Field label="초기자금(KRW)" value={state.initialCash} onChange={state.setInitialCash} />
          <ProfileField value={state.strategyProfile} onChange={state.setStrategyProfile} />
        </div>
        <p className="kr-backtest-profile-note">
          기본 kr_paper는 국내장 주문/체결 샘플 확보용입니다. LIVE 주문 프로필이 아닙니다.
        </p>
        <div className="kr-backtest-actions">
          <button className="kr-backtest-run" disabled={state.loading} onClick={state.run}>
            {state.loading ? '실행 중...' : '수동 종목으로 실행'}
          </button>
          <button className="kr-backtest-secondary" disabled={state.loading || state.watchlistLoading} onClick={state.runCurrentWatchlist}>
            현재 KR watchlist로 실행
          </button>
          <button className="kr-backtest-secondary" disabled={state.watchlistLoading} onClick={state.importHoldings}>
            Toss 보유종목 가져오기
          </button>
        </div>
      </section>
      {state.result ? <Summary result={state.result} /> : <EmptyState />}
    </>
  );
}

function Field({ label, value, onChange, type = 'text' }: FieldProps) {
  return (
    <label className="kr-backtest-field">
      <span>{label}</span>
      <input type={type} value={value} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}

function ProfileField({ value, onChange }: ProfileFieldProps) {
  return (
    <label className="kr-backtest-field">
      <span>전략 프로필</span>
      <select value={value} onChange={(event) => onChange(event.target.value)}>
        <option value="kr_paper">kr_paper - 국내 검증 샘플</option>
        <option value="paper">paper - 기존 미국장 기준</option>
        <option value="live">live - 보수 기준(검증용)</option>
      </select>
    </label>
  );
}

function Summary({ result }: { result: KrBacktestSummary }) {
  return (
    <section className="kr-backtest-card">
      <h2>실행 결과</h2>
      <div className="kr-backtest-metrics">
        <Metric label="상태" value={result.status} />
        <Metric label="판단" value={String(result.decisions.total)} />
        <Metric label="주문" value={String(result.orders.total)} />
        <Metric label="체결" value={String(result.fills.total)} />
      </div>
      <dl className="kr-backtest-details">
        <Row label="Run ID" value={result.run_id} />
        <Row label="프로필" value={result.strategy_profile} />
        <Row label="종목" value={symbolLabels(result)} />
        <Row label="Action" value={actions(result.decisions.by_action)} />
        <Row label="Dataset Hash" value={shortHash(result.dataset_hash)} />
        <Row label="경고" value={result.warnings.join(', ') || '없음'} />
      </dl>
    </section>
  );
}

function EmptyState() {
  return <section className="kr-backtest-empty">아직 실행 결과가 없습니다. 실제 Toss KR 데이터 수집 후 결과가 표시됩니다.</section>;
}

function Metric({ label, value }: { label: string; value: string }) {
  return <div className="kr-backtest-metric"><span>{label}</span><strong>{value}</strong></div>;
}

function Row({ label, value }: { label: string; value: string }) {
  return <><dt>{label}</dt><dd>{value}</dd></>;
}

function actions(values: Record<string, number>) {
  return ['BUY', 'SELL', 'HOLD'].map((key) => `${key} ${values[key] || 0}`).join(' / ');
}

function symbolLabels(result: KrBacktestSummary) {
  const rows = result.symbols_display?.length
    ? result.symbols_display
    : result.symbols.map((symbol) => ({ symbol, name: null }));
  return rows.map((row) => row.name ? `${row.name} (${row.symbol})` : row.symbol).join(', ');
}

function shortHash(value: string | null) {
  return value ? `${value.slice(0, 10)}...` : '데이터 없음';
}

interface KrBacktestPanelProps {
  showHeader?: boolean;
}

interface FieldProps {
  label: string;
  value: string;
  type?: string;
  onChange: (value: string) => void;
}

interface ProfileFieldProps {
  value: string;
  onChange: (value: string) => void;
}
